import { create } from 'zustand'

/**
 * View state the Alert Window panel shares with its title-bar controls.
 *
 * The window name, the live label, Pause and the chrome actions live in the
 * panel HEADER (rendered by the workspace mosaic via `renderToolbarExtras` /
 * `renderToolbarExtrasRight`); the matches live in the panel BODY. The body
 * publishes the live label here by panelId. Same shape as
 * `useGridRowCountStore`: transient, no persistence. The window itself lives
 * in panel state.
 */

/** What the live label says; the body publishes it once it knows. */
export type AlertWindowsLiveStatus =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'live' | 'paused'; count: string }

interface AlertWindowsViewState {
  status: Record<string, AlertWindowsLiveStatus | undefined>
  setStatus: (panelId: string, status: AlertWindowsLiveStatus | undefined) => void
  /** Drop everything held for a panel; called when the panel is closed. */
  clearPanel: (panelId: string) => void
}

const sameStatus = (a?: AlertWindowsLiveStatus, b?: AlertWindowsLiveStatus) =>
  a === b ||
  (!!a &&
    !!b &&
    a.kind === b.kind &&
    ('count' in a ? a.count : null) === ('count' in b ? b.count : null))

export const useAlertWindowsViewStore = create<AlertWindowsViewState>((set) => ({
  status: {},
  setStatus: (panelId, next) =>
    set((s) =>
      sameStatus(s.status[panelId], next) ? s : { status: { ...s.status, [panelId]: next } },
    ),
  clearPanel: (panelId) =>
    set((s) => {
      if (!(panelId in s.status)) return s
      const { [panelId]: _s, ...status } = s.status
      return { status }
    }),
}))
